'use client'

import { AnimatePresence, LazyMotion, domAnimation, m } from 'framer-motion'
import { useState } from 'react'

import { SectionHead } from '@/ui/components/marks'
import { Action } from '@/ui/components/ui/action'

const FAQ_DATA = [
  {
    id: 'services',
    question: 'Quels travaux réalisez-vous ?',
    answer:
      "Nous réalisons un large panel de travaux tous corps d'État : rénovations intérieures (cuisine, salle de bain), plomberie (installations et réparations), électricité (mise aux normes, tableaux, éclairage, dépannage), menuiserie (portes, fenêtres, agencements), vitrerie (remplacement, sécurisation), serrurerie (ouverture, remplacement), peintures et revêtements, carrelage et faïence, isolation et cloisonnement, entretien et dépannage de volets roulants (déblocage, motorisation, remplacement), ainsi que les travaux de toiture (couverture tuiles/ardoises, zinguerie, recherche et réparation d'infiltrations, réfection partielle ou totale, démoussage, étanchéité, isolation des combles et pose de fenêtres de toit).",
  },
  {
    id: 'zones',
    question: 'Dans quelles villes intervenez-vous ?',
    answer:
      "Basée au cœur de Septeuil (78790), notre équipe intervient en priorité sur notre commune et ses alentours (Houdan, Mantes-la-Jolie) pour garantir une réactivité maximale. Pour une urgence plombier, un dépannage électrique ou un besoin de serrurier sur ce secteur, nous sommes sur place en moins de 30 minutes. Au-delà — Poissy, Versailles, Saint-Germain-en-Laye, Dreux — comptez sous 60 minutes. Nous couvrons l'ensemble des Yvelines (78) pour les travaux de rénovation.",
  },
  {
    id: 'delais',
    question: "Quels sont vos délais d'intervention ?",
    answer:
      "Notre force est la proximité. En tant qu'artisans locaux à Septeuil, nous assurons un dépannage express 7j/7. Comptez environ 30 minutes pour l'arrivée d'un serrurier (ouverture de porte) ou d'un plombier (recherche de fuite) si vous êtes dans le secteur de Septeuil, et sous 60 minutes sur le reste des Yvelines. Pour l'électricité et les pannes de courant, nous intervenons également dans l'heure. Nos tarifs standard s'appliquent du lundi au vendredi de 8h à 18h ; en soirée, le week-end et les jours fériés, une majoration de 50 % s'ajoute.",
  },
  {
    id: 'assurances',
    question: 'Êtes-vous assurés et vos travaux sont-ils garantis ?',
    answer:
      "Oui, nous disposons d'une assurance responsabilité civile professionnelle et d'une garantie décennale pour les travaux concernés. Les matériaux sont posés conformément aux DTU en vigueur et bénéficient des garanties fabricants.",
  },
  {
    id: 'devis',
    question: 'Vos devis sont-ils gratuits ?',
    answer:
      "Oui, absolument. Que vous habitiez à Septeuil ou ailleurs dans le 78, le devis est gratuit. Pour les urgences (comme un changement de serrure ou un débouchage de canalisation), nous vous annonçons un tarif clair par téléphone avant le déplacement du technicien. Pas de surprise avec Nicobat.",
  },
] as const

export default function FAQ() {
  const [openSection, setOpenSection] = useState<string | null>(null)

  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: FAQ_DATA.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  }

  return (
    <LazyMotion features={domAnimation}>
      <section className="bg-chalk band" id="faq">
        <script
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }}
        />

        <div className="shell">
          <div className="grid gap-12 desktop:grid-cols-12 desktop:gap-16">
            <div className="desktop:col-span-4">
              {/* La colonne d'appui reste en vue pendant la lecture */}
              <div className="desktop:sticky desktop:top-28">
                <SectionHead
                  cote="Foire aux questions"
                  title="Tout ce que vous devez savoir sur nos services"
                />

                <div className="mt-10 flex flex-wrap gap-3">
                  <Action href="#contact" variant="solid">
                    Nous contacter
                  </Action>
                  <Action href="#services" variant="outline">
                    Nos prestations
                  </Action>
                </div>

                <p className="sr-only">
                  Entreprise générale du bâtiment dans les Yvelines (78),
                  spécialisée en rénovation tous corps d&apos;état, aménagement
                  intérieur, dépannage et mise aux normes pour particuliers et
                  professionnels.
                </p>
              </div>
            </div>

            <div className="desktop:col-span-7 desktop:col-start-6">
              <dl className="border-t border-ink/15">
                {FAQ_DATA.map((item) => {
                  const isOpen = openSection === item.id

                  return (
                    <div key={item.id} className="border-b border-ink/15">
                      <dt>
                        <button
                          type="button"
                          className="group flex w-full items-start justify-between gap-6 py-6 text-left"
                          onClick={() =>
                            setOpenSection(isOpen ? null : item.id)
                          }
                          aria-expanded={isOpen}
                          aria-controls={`faq-${item.id}`}
                        >
                          <span className="font-display text-lg tablet:text-xl font-bold tracking-tight text-ink">
                            {item.question}
                          </span>

                          {/* Indicateur + / − : un seul trait pivote */}
                          <span
                            aria-hidden="true"
                            className="relative mt-2 block size-3 shrink-0 text-graphite transition-colors group-hover:text-signal"
                          >
                            <span className="absolute left-0 top-1/2 h-px w-full bg-current" />
                            <span
                              className={`absolute left-1/2 top-0 h-full w-px bg-current transition-transform duration-300 ${
                                isOpen ? 'rotate-90' : ''
                              }`}
                            />
                          </span>
                        </button>
                      </dt>

                      <AnimatePresence initial={false}>
                        {isOpen && (
                          <m.dd
                            id={`faq-${item.id}`}
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: 'auto', opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            transition={{
                              duration: 0.4,
                              ease: [0.22, 1, 0.36, 1],
                            }}
                            className="overflow-hidden"
                          >
                            <p className="max-w-[68ch] pb-8 pr-8 text-[0.9375rem] leading-relaxed text-anthracite">
                              {item.answer}
                            </p>
                          </m.dd>
                        )}
                      </AnimatePresence>
                    </div>
                  )
                })}
              </dl>
            </div>
          </div>
        </div>
      </section>
    </LazyMotion>
  )
}
